import React, { useMemo, useState, useContext, useEffect } from 'react';
import { useUrlDateType } from '../Hooks/useUrlDateType';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { useViewFilters, usePaginationReset } from '../Hooks/useViewFilters';
import { useViewQuery } from '../Hooks/useViewQuery';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { MaterialReactTable } from 'material-react-table';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { Box, ThemeProvider } from '@mui/material';
import { formatFileSize } from '../utils/CommonUtils';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import CategoryIcon from '@mui/icons-material/Category';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import './LoggerComponent.css';

const PREF_STORAGE_KEY = 'logger_filter_preferences';
const DEFAULT_FIELDS = {
  vin: [],
  mac_id: [],
  category: [],
  file_type: [],
};

const filterConfig = {
  dateFields: [createDateConfig('start_datetime', 'end_datetime', { startParam: 'startDate', endParam: 'endDate', allowEmpty: true })],
  debounceDelay: 1000,
};

const LoggerComponent = ({ activeTheme }) => {
  const [dateType, handleDateTypeChange] = useUrlDateType();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'datetime', desc: true }]);
  const mapping = { vin: 'vin', mac_id: 'mac_id', file_type: 'file_type', category: 'category' };

  const {
    singleValueFilters,
    debouncedSingleValueFilters,
    handleSingleFilterChange,
    handleSavePreferences,
    getOtherFilters,
    mergeField,
  } = useViewFilters({
    storageKey: PREF_STORAGE_KEY,
    defaultFields: DEFAULT_FIELDS,
    urlMapping: mapping,
    debounceDelay: 1000,
  });

  const { filters, debouncedFilters, handleDateRangeChange } = useUrlFilters(filterConfig);
  const { formatDateTime, createIsoStringWithTimezone } = useContext(TimezoneContext);

  const mergedFilters = useMemo(() => {
    return {
      ...debouncedFilters,
      vin: mergeField(debouncedSingleValueFilters.vin, debouncedFilters.vin),
      mac_id: mergeField(debouncedSingleValueFilters.mac_id, debouncedFilters.mac_id),
      category: mergeField(debouncedSingleValueFilters.category, debouncedFilters.category),
      file_type: mergeField(debouncedSingleValueFilters.file_type, debouncedFilters.file_type),
    };
  }, [debouncedFilters, debouncedSingleValueFilters, mergeField]);

  usePaginationReset(mergedFilters, dateType, setPagination);

  const filesQuery = useViewQuery({
    queryKey: 'files',
    mergedFilters,
    pagination,
    sorting,
    dateType,
    urlMapping: mapping,
    createIsoStringWithTimezone,
    endpoint: 'loggers',
  });

  const data = useMemo(() => filesQuery.data?.results || [], [filesQuery.data]);

  const columns = useMemo(() => [
    { accessorKey: 'vin', header: 'VIN' },
    { accessorKey: 'mac_id', header: 'MAC ID', size: 220 },
    { accessorKey: 'filename', header: 'Filename' },
    { accessorKey: 'file_id', header: 'File ID' },
    { accessorKey: 'category', header: 'Category', size: 220, Cell: ({ row }) => row.original.category || row.original.file_type },
    {
      accessorKey: 'datetime',
      header: 'Created on Device',
      size: 280,
      Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss'),
    },
    {
      accessorKey: 'datetime_upload',
      header: 'Uploaded to Cloud',
      size: 280,
      Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss'),
    },
    {
      accessorKey: 'file_size',
      header: 'Size',
      size: 130,
      Cell: ({ cell }) => formatFileSize(cell.getValue()),
      sortingFn: (rowA, rowB) => {
        const a = parseInt(rowA.original.file_size, 10) || 0;
        const b = parseInt(rowB.original.file_size, 10) || 0;
        return a - b;
      },
    },
  ], [formatDateTime]);

  const { table, setRowSelection } = useReusableTable({
    columns,
    data,
    rowCount: filesQuery.data?.count || 0,
    isLoading: filesQuery.isFetching,
    enableRowSelection: true,
    enableRowExpansion: true,
    enableCompareFiles: true,
    enableRowVirtualization: true,
    initialPageSize: 100,
    maxHeight: 'calc(100vh - 250px)',
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 250px)' } },
    enableDefaultPreview: true,
    enableDefaultDownload: true,
    enableDefaultAddToCart: true,
    getRowId: (row) => row.id,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    muiPaginationProps: { color: 'primary', rowsPerPageOptions: [100, 200, 500, 1000]},
  });

  useEffect(() => {
    setRowSelection({});
  }, [mergedFilters, pagination.pageIndex, setRowSelection]);



  const dropdowns = useMemo(() => (
    <>
      <Box sx={{ mt: 2 }}>
        <FilterDropdown
          field="vin"
          label="VIN"
          value={singleValueFilters.vin}
          onChange={(value) => handleSingleFilterChange('vin', value)}
          apiBaseUrl="/api/filters"
          multiple={true}
          icon={DirectionsCarIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('vin')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="mac_id"
          label="MAC ID"
          value={singleValueFilters.mac_id}
          onChange={(value) => handleSingleFilterChange('mac_id', value)}
          apiBaseUrl="/api/filters"
          multiple={true}
          icon={RouterIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('mac_id')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="category"
          label="Category"
          value={singleValueFilters.category}
          onChange={(value) => handleSingleFilterChange('category', value)}
          apiBaseUrl="/api/filters"
          multiple={true}
          icon={CategoryIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('category')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="file_type"
          label="File Type"
          value={singleValueFilters.file_type}
          onChange={(value) => handleSingleFilterChange('file_type', value)}
          apiBaseUrl="/api/filters"
          multiple={true}
          icon={InsertDriveFileIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('file_type')}
          fieldMapping={mapping}
        />
      </Box>
    </>
  ), [
    singleValueFilters,
    handleSingleFilterChange,
    debouncedFilters,
    dateType,
    createIsoStringWithTimezone,
    getOtherFilters,
    mapping
  ]);

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="logger-main-container">
        <Box>
          <FiltersComponent
            activeTheme={activeTheme}
            dateRangePicker={{ filters, handleDateRangeChange }}
            onSaveSearch={handleSavePreferences}
            storageKey={PREF_STORAGE_KEY}
            dropdowns={dropdowns}
            dateTypeSelector={{ dateType, handleDateTypeChange }}
          />
        </Box>
        <Box className="logger-content">
          <MaterialReactTable table={table} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LoggerComponent;
